import { ENDPOINTS } from 'config/endpoints';
import {
  QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import CronoError from 'crono-fe-common/types/error';
import client from 'utils/clients/client';
import { Request } from 'crono-fe-common/types/request';
import { Response, ResponsePaginated } from 'crono-fe-common/types/response';
import { Account } from 'crono-fe-common/types/account';
import { AccountInfoInputs } from 'crono-fe-common/types/DTO/accountInfoInputs';
import { useCrmPushActions } from 'context/crmPush';

export default function useEditAccount() {
  const queryClient = useQueryClient();
  const { resumePolling } = useCrmPushActions();

  const editAccount = (accountInfo: AccountInfoInputs) => {
    const request: Request = {
      url: `${ENDPOINTS.account.main}`,
      config: {
        method: 'patch',
        data: { data: accountInfo },
      },
    };
    return client(request);
  };

  const { data, mutate, ...rest } = useMutation<
    Response<Account>,
    CronoError,
    AccountInfoInputs
  >(editAccount, {
    onSettled: (_r, error, variables) => {
      const externalValuesProvided =
        variables?.externalValues &&
        Object.keys(variables.externalValues).length > 0;

      if (error) {
        invalidateEditAccountQueries(queryClient, externalValuesProvided);
      } else if (isExternalValuesOnlyUpdate(variables)) {
        patchAccountExternalValuesInCache(queryClient, variables);
      } else {
        invalidateEditAccountQueries(queryClient, externalValuesProvided);
      }

      if (externalValuesProvided || variables.status) {
        resumePolling();
      }
    },
  });

  return { data, mutate, ...rest };
}

// True only when the patch contains exclusively externalValues — no name,
// status, website, or any other field that would affect other list/detail views.
function isExternalValuesOnlyUpdate(variables: AccountInfoInputs): boolean {
  const { accountId: _id, externalValues, ...rest } = variables;
  if (!externalValues || Object.keys(externalValues).length === 0) return false;
  return Object.values(rest).every((v) => v === undefined || v === null);
}

// Patches the affected ExternalValue entries directly in the
// useSearchAccounts and useGetAccount caches without triggering a refetch.
// Unchanged ExternalValue objects keep the same reference so memoized
// components that depend on them don't re-render.
// A property the account has no value for yet is simply absent from the cached
// list, so it gets appended rather than patched in place.
function patchAccountExternalValuesInCache(
  queryClient: QueryClient,
  variables: AccountInfoInputs,
): void {
  const { accountId, externalValues } = variables;
  if (!externalValues) return;

  const patchAccount = (account: Account): Account => {
    if (account.objectId !== accountId) return account;

    const currentValues = account.externalValues ?? [];
    const existingPropertyIds = new Set(
      currentValues.map((ev) => ev.externalPropertyId),
    );

    return {
      ...account,
      externalValues: [
        ...currentValues.map((ev) => {
          const newValue = externalValues[ev.externalPropertyId];
          return newValue !== undefined ? { ...ev, value: newValue } : ev;
        }),
        ...Object.entries(externalValues)
          .filter(([id]) => !existingPropertyIds.has(Number(id)))
          .map(([id, value]) => ({
            externalPropertyId: Number(id),
            value: value ?? null,
          })),
      ],
    };
  };

  queryClient.setQueriesData<ResponsePaginated<Account[]>>(
    {
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === ENDPOINTS.account.search.main,
    },
    (old) => {
      const accounts = old?.data?.data;
      if (!old || !accounts?.some((a) => a.objectId === accountId)) {
        return old;
      }

      return {
        ...old,
        data: { ...old.data, data: accounts.map(patchAccount) },
      } as ResponsePaginated<Account[]>;
    },
  );

  queryClient.setQueriesData<Response<Omit<Account, 'prospects'>>>(
    {
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === ENDPOINTS.account.main &&
        query.queryKey[1] === accountId,
    },
    (old) => {
      if (!old?.data?.data) return old;

      return {
        ...old,
        data: { ...old.data, data: patchAccount(old.data.data as Account) },
      } as Response<Omit<Account, 'prospects'>>;
    },
  );
}

export async function invalidateEditAccountQueries(
  queryClient: QueryClient,
  invalidateStrategyQueries?: boolean,
  accountIds?: string[],
) {
  const accountQueries = accountIds?.length
    ? accountIds.map((accountId) =>
        queryClient.invalidateQueries([ENDPOINTS.account.main, accountId]),
      )
    : [
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey.toString().startsWith(ENDPOINTS.account.main),
        }),
      ];

  const promises = [
    ...accountQueries,
    queryClient.invalidateQueries([ENDPOINTS.account.search.main]),
    queryClient.invalidateQueries([ENDPOINTS.task.search]),
    queryClient.invalidateQueries([ENDPOINTS.task.main]),
    queryClient.invalidateQueries([ENDPOINTS.prospect.search]),
  ];

  if (invalidateStrategyQueries) {
    promises.push(
      queryClient.invalidateQueries([ENDPOINTS.strategy.search]),
      queryClient.invalidateQueries([ENDPOINTS.strategy.prospect]),
      queryClient.invalidateQueries([ENDPOINTS.prospect.search.strategy]),
    );
  }

  await Promise.all(promises);
}
